import { useCallback, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Sync } from "@mui/icons-material";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import { useGerenteDeEscala } from "@/hooks/useGerenteDeEscala";
import {
  listProvedoresIntegracao,
  sincronizarIntegracao,
  statusSincronizacao,
  type StatusSincronizacao,
} from "@/api/escala.service";

const INTERVALO_POLLING_MS = 3000;

// Botão de sincronizar com o sistema externo (S9); o resultado fica no histórico. Só
// aparece para Gerente ou acima e só com a integração ativa — o nome do sistema
// vem do provedor configurado, a tela não conhece nenhum.
export function EscalaSincronizacaoPanel({ onConcluida }: { onConcluida?: () => void }) {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { podeVerRelatorios: gerente, carregando } = useGerenteDeEscala();

  const [status, setStatus] = useState<StatusSincronizacao | null>(null);
  const [nomeSistema, setNomeSistema] = useState("sistema externo");
  const [iniciando, setIniciando] = useState(false);
  const rodandoAntes = useRef(false);
  // Ref para que um onConcluida novo a cada render do pai não recrie `carregar` e refaça a consulta.
  const aoConcluir = useRef(onConcluida);
  aoConcluir.current = onConcluida;

  const carregar = useCallback(async () => {
    try {
      const s = await statusSincronizacao(clientId);
      setStatus(s);
      // Recarrega a lista quando uma execução que estava rodando termina.
      if (rodandoAntes.current && s.status !== "RODANDO") aoConcluir.current?.();
      rodandoAntes.current = s.status === "RODANDO";
    } catch {
      // Sem permissão ou fora do ar: o painel simplesmente não aparece. Com uma
      // execução em curso, uma falha pontual não esconde o botão nem para a consulta.
      if (!rodandoAntes.current) setStatus(null);
    }
  }, [clientId]);

  useEffect(() => {
    if (!gerente) return;
    carregar();
  }, [gerente, carregar]);

  useEffect(() => {
    if (!status?.provedor) return;
    let cancelado = false;
    listProvedoresIntegracao(clientId)
      .then((ps) => !cancelado && setNomeSistema(ps.find((p) => p.id === status.provedor)?.nome ?? "sistema externo"))
      .catch(() => {}); // só o administrador lista; o nome genérico basta
    return () => {
      cancelado = true;
    };
  }, [clientId, status?.provedor]);

  const rodando = status?.status === "RODANDO";
  useEffect(() => {
    if (!rodando) return;
    const t = setInterval(carregar, INTERVALO_POLLING_MS);
    return () => clearInterval(t);
  }, [rodando, carregar]);

  if (carregando || !gerente || !status?.integracao_ativa) return null;

  const iniciar = async () => {
    setIniciando(true);
    try {
      await sincronizarIntegracao(clientId);
      await carregar();
    } catch (e: any) {
      notify(e?.message || "Erro ao iniciar a sincronização", "error");
      carregar();
    } finally {
      setIniciando(false);
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
      <Button
        variant="outlined"
        startIcon={rodando || iniciando ? <CircularProgress size={18} /> : <Sync />}
        onClick={iniciar}
        disabled={rodando || iniciando}
      >
        {`Sincronizar com ${nomeSistema}`}
      </Button>
      {rodando && (
        <Typography variant="body2" color="text.secondary">
          Isso pode levar alguns minutos. Você pode continuar usando o sistema.
        </Typography>
      )}
    </Stack>
  );
}
